import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Trophy, Star, Crown, Flame, Zap, Gift, Loader2 } from 'lucide-react';

const DriverLoyalty = () => {
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [peakData, setPeakData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('reddi_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const baseUrl = process.env.REACT_APP_BACKEND_URL;

      const [loyaltyRes, streakRes, peakRes] = await Promise.all([
        fetch(`${baseUrl}/api/driver/loyalty`, { headers }),
        fetch(`${baseUrl}/api/driver/streak`, { headers }),
        fetch(`${baseUrl}/api/driver/peak-hours`, { headers })
      ]);

      setLoyaltyData(await loyaltyRes.json());
      setStreakData(await streakRes.json());
      setPeakData(await peakRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const tierColors = {
    bronze: 'from-amber-600 to-amber-700',
    silver: 'from-slate-400 to-slate-500'
  };

  const tierIcons = {
    bronze: Star,
    silver: Crown
  };

  const TierIcon = tierIcons[loyaltyData?.current_tier] || Star;

  const loyaltyProgress = loyaltyData?.next_tier 
    ? ((loyaltyData.total_rides - (loyaltyData.tiers[loyaltyData.current_tier]?.min_rides || 0)) / 
       (loyaltyData.tiers[loyaltyData.next_tier]?.min_rides - (loyaltyData.tiers[loyaltyData.current_tier]?.min_rides || 0))) * 100
    : 100;

  return (
    <div className="p-6" data-testid="driver-loyalty-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Driver Rewards
        </h1>

        {/* Current Tier Card */}
        <Card className={`mb-6 bg-gradient-to-br ${tierColors[loyaltyData?.current_tier] || tierColors.bronze} border-0 text-white overflow-hidden`}>
          <CardContent className="p-8 relative">
            <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
              <TierIcon className="w-full h-full" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <TierIcon className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm uppercase tracking-wide">Driver Tier</p>
                  <p className="text-4xl font-display font-bold capitalize">{loyaltyData?.current_tier || 'Bronze'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-8 mt-8">
                <div>
                  <p className="text-white/80 text-sm">Fare Bonus</p>
                  <p className="text-3xl font-display font-bold">{loyaltyData?.bonus_percent || 0}%</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Total Rides</p>
                  <p className="text-3xl font-display font-bold">{loyaltyData?.total_rides || 0}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Priority Rides</p>
                  <p className="text-3xl font-display font-bold">{loyaltyData?.priority_rides ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {loyaltyData?.perks?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {loyaltyData.perks.map((perk, index) => (
                    <Badge key={index} className="bg-white/20 text-white border-0">
                      {perk}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress to Next Tier */}
        {loyaltyData?.next_tier && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">
                Progress to {loyaltyData.next_tier}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Progress value={loyaltyProgress} className="h-4" />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                  {loyaltyData.total_rides} rides completed
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {loyaltyData.rides_to_next_tier} more to {loyaltyData.next_tier}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Streak & Peak Hours */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Streak Card */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Ride Streak
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-3">
                  <span className="text-4xl font-display font-bold text-orange-600">{streakData?.current_streak || 0}</span>
                </div>
                <p className="text-slate-500 dark:text-slate-400">Current Streak</p>
                {streakData?.current_bonus_percent > 0 && (
                  <Badge className="mt-2 bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400">
                    +{streakData.current_bonus_percent}% Bonus Active
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                    {streakData?.longest_streak || 0}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Longest Streak</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                    {streakData?.next_milestone || '-'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Next Milestone</p>
                </div>
              </div>

              {/* Streak Milestones */}
              <div className="mt-4 space-y-2">
                {streakData?.streak_bonuses && Object.entries(streakData.streak_bonuses).map(([rides, bonus]) => (
                  <div 
                    key={rides} 
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      streakData.current_streak >= parseInt(rides) 
                        ? 'bg-green-50 dark:bg-green-900/20' 
                        : 'bg-slate-50 dark:bg-slate-700/50'
                    }`}
                  >
                    <span className="text-sm text-slate-600 dark:text-slate-300">{rides} rides</span>
                    <span className={`text-sm font-semibold ${
                      streakData.current_streak >= parseInt(rides)
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      +{bonus}% bonus
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Peak Hours Card */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Peak Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              {peakData?.current_status?.is_peak ? (
                <div className="text-center mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
                  <Zap className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
                  <p className="text-lg font-semibold text-yellow-700 dark:text-yellow-400">
                    Peak Hour Active!
                  </p>
                  <p className="text-3xl font-display font-bold text-yellow-600">
                    {peakData.current_status.multiplier}x
                  </p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-500">
                    Earning multiplier
                  </p>
                </div>
              ) : (
                <div className="text-center mb-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                  <p className="text-slate-500 dark:text-slate-400">No peak hour active</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Check schedule below for upcoming peaks
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {peakData?.peak_hours && Object.entries(peakData.peak_hours).map(([period, config]) => (
                  <div 
                    key={period}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      peakData.current_status?.period === period
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                        : 'bg-slate-50 dark:bg-slate-700/50'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white capitalize">{period}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {config.start}:00 - {config.end}:00
                      </p>
                    </div>
                    <Badge className={
                      peakData.current_status?.period === period
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                        : 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                    }>
                      {config.multiplier}x
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Tiers Info */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Trophy className="w-5 h-5 text-red-600" />
              Driver Tiers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loyaltyData?.tiers && Object.entries(loyaltyData.tiers).map(([tier, config]) => {
                const Icon = tierIcons[tier] || Star;
                const isCurrentTier = tier === loyaltyData?.current_tier;
                const isAchieved = loyaltyData?.total_rides >= config.min_rides;

                return (
                  <div
                    key={tier}
                    className={`
                      flex items-center justify-between p-4 rounded-xl border-2 transition-all
                      ${isCurrentTier 
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                        : isAchieved
                          ? 'border-green-500/50 bg-green-50/50 dark:bg-green-900/10'
                          : 'border-slate-200 dark:border-slate-700'
                      }
                    `}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-12 h-12 rounded-xl flex items-center justify-center
                        ${isAchieved ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'}
                      `}>
                        <Icon className={`w-6 h-6 ${tier === 'silver' ? 'text-slate-500' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <p className="font-display font-bold uppercase text-slate-900 dark:text-white">
                          {tier}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {config.min_rides}+ rides
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-display font-bold text-slate-900 dark:text-white">
                        +{config.bonus_percent * 100}%
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">bonus</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverLoyalty;
